/**
 * Sales Service
 *
 * Quotes, invoices, payments, and delivery notes management.
 */

import apiClient, {
  APIResponse,
  PaginatedResponse,
  unwrapResponse,
  unwrapPaginatedResponse,
} from './apiClient';
import { ENDPOINTS, PAGINATION } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type SaleType = 'VENTE' | 'LOCATION' | 'LOA';
export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED';
export type InvoiceStatus = 'DRAFT' | 'VALIDATED' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELLED';
export type InvoiceType = 'PROFORMA' | 'DEFINITIVE' | 'AVOIR';
export type PaymentMethod = 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'MOBILE_MONEY_MTN' | 'MOBILE_MONEY_MOOV';
export type DeliveryStatus = 'PENDING' | 'IN_TRANSIT' | 'PARTIAL' | 'DELIVERED' | 'CANCELLED';

export interface QuoteItem {
  id?: string;
  product_id: string;
  designation: string;
  product_reference?: string;
  product_nom?: string;
  quantite: number;
  prix_unitaire: number;
  taux_remise: number;
  montant_ht: number;
  ordre?: number;
  /** Quantity already committed on non-cancelled BLs (invoice lines only). */
  quantite_livree?: number;
  /** Remaining quantity to deliver = quantite - quantite_livree. */
  quantite_restante?: number;
}

export interface Quote {
  id: string;
  code: string;
  type_vente: SaleType;
  client_id: string;
  client_nom?: string;
  commercial_id: string;
  commercial_nom?: string;
  site_stock_id: string;
  site_stock_nom?: string;
  site_stock_code?: string;
  date_devis: string;
  date_validite: string;
  status: QuoteStatus;
  lignes: QuoteItem[];
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  conditions?: string;
  notes?: string;
  // LOCATION fields
  date_debut_location?: string;
  date_fin_prevue_location?: string;
  montant_caution?: number;
  tarif_journalier?: number;
  // LOA fields
  duree_mois_loa?: number;
  taux_interet_annuel_loa?: number;
  apport_initial_loa?: number;
  valeur_residuelle_loa?: number;
  created_at: string;
  updated_at: string;
}

export interface QuoteLineCreate {
  product_id: string;
  designation: string;
  quantite: number;
  prix_unitaire: number;
  taux_remise?: number;
}

export interface QuoteCreate {
  client_id: string;
  /** Delivery site chosen by the commercial at quote initiation. Required. */
  site_stock_id: string;
  type_vente?: SaleType;
  date_validite: string;
  lignes: QuoteLineCreate[];
  notes?: string;
  conditions?: string;
  // LOCATION fields
  date_debut_location?: string;
  date_fin_prevue_location?: string;
  montant_caution?: number;
  tarif_journalier?: number;
  // LOA fields
  duree_mois_loa?: number;
  taux_interet_annuel_loa?: number;
  apport_initial_loa?: number;
  valeur_residuelle_loa?: number;
}

export type PaymentStatus = 'PENDING' | 'VALIDATED' | 'REJECTED';

export interface InvoicePaymentSummary {
  id: string;
  code: string;
  date_paiement: string;
  montant: number;
  mode_paiement: PaymentMethod;
  status: PaymentStatus;
  reference?: string;
}

export interface Invoice {
  id: string;
  code: string;
  type_vente?: SaleType;
  devis_id?: string;
  client_id: string;
  client_nom?: string;
  commercial_id: string;
  commercial_nom?: string;
  /** Delivery site propagated from the source quote; pre-fills the auto-BL picker. */
  site_stock_id?: string;
  site_stock_nom?: string;
  site_stock_code?: string;
  type_facture: InvoiceType;
  date_facture: string;
  date_echeance: string;
  status: InvoiceStatus;
  lignes: QuoteItem[];
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  montant_paye: number;
  montant_restant: number;
  /**
   * Sum of payments awaiting validation. Already reserved on the balance by the
   * backend, but NOT reflected in montant_paye/montant_restant (those move only
   * on validation) — so the truly enterable amount is montant_restant - this.
   * Detail endpoint only.
   */
  montant_en_attente?: number;
  /** Payments attached to the invoice, incl. PENDING. Detail endpoint only. */
  paiements?: InvoicePaymentSummary[];
  mentions_legales?: string;
  notes?: string;
  date_debut_location?: string;
  date_fin_location?: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceCreate {
  client_id: string;
  devis_id?: string;
  type_facture: InvoiceType;
  type_vente?: SaleType;
  date_echeance: string;
  lignes: QuoteLineCreate[];
  taux_remise_global?: number;
  mentions_legales?: string;
  notes?: string;
  is_exonere_tva?: boolean;
  reference_exoneration?: string;
}

export interface Payment {
  id: string;
  code: string;
  facture_id: string;
  facture_code?: string;
  montant: number;
  date_paiement: string;
  mode_paiement: PaymentMethod;
  status: PaymentStatus;
  reference?: string;
  banque?: string;
  notes?: string;
  enregistre_par_id: string;
  enregistre_par_nom?: string;
  created_at: string;
}

export interface PaymentCreate {
  facture_id: string;
  montant: number;
  date_paiement?: string;
  mode_paiement: PaymentMethod;
  reference?: string;
  banque?: string;
  numero_telephone?: string;
  transaction_id?: string;
  notes?: string;
  /**
   * Optional source depot for the BL auto-generated when this payment
   * fully settles a VENTE invoice. Omitted -> backend falls back to
   * the default site.
   */
  site_stock_id?: string;
}

export interface DeliveryItem {
  product_id: string;
  product_reference?: string;
  product_nom?: string;
  designation?: string;
  quantite_commandee: number;
  quantite_livree: number;
}

export interface DeliveryNote {
  id: string;
  code: string;
  type_vente?: SaleType;
  facture_id: string;
  facture_code?: string;
  client_id: string;
  client_nom?: string;
  date_livraison: string;
  adresse_livraison?: string;
  status: DeliveryStatus;
  lignes: DeliveryItem[];
  livreur_nom?: string;
  signature_client?: string;
  signature_livreur?: string;
  notes?: string;
  contrat_location_id?: string;
  contrat_loa_id?: string;
  created_at: string;
}

export interface DeliveryNoteCreate {
  facture_id: string;
  date_livraison?: string;
  adresse_livraison?: string;
  lignes: {
    facture_ligne_id?: string;
    product_id: string;
    designation: string;
    quantite_commandee: number;
    quantite_livree: number;
  }[];
  livreur_nom?: string;
  notes?: string;
  site_stock_id: string;
}

// =============================================================================
// List Params
// =============================================================================

export interface QuoteListParams {
  page?: number;
  page_size?: number;
  search?: string;
  client_id?: string;
  status?: QuoteStatus;
  type_vente?: SaleType;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface DeliveryNoteListParams {
  page?: number;
  page_size?: number;
  search?: string;
  facture_id?: string;
  client_id?: string;
  status?: DeliveryStatus;
  type_vente?: SaleType;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface InvoiceListParams {
  page?: number;
  page_size?: number;
  search?: string;
  client_id?: string;
  status?: InvoiceStatus;
  type_facture?: InvoiceType;
  type_vente?: SaleType;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  overdue_only?: boolean;
}

export interface PaymentListParams {
  page?: number;
  page_size?: number;
  invoice_id?: string;
  mode_paiement?: PaymentMethod;
  date_from?: string;
  date_to?: string;
}

// =============================================================================
// Sales Service
// =============================================================================

export const salesService = {
  // ---------------------------------------------------------------------------
  // Quotes
  // ---------------------------------------------------------------------------

  async listQuotes(params: QuoteListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<Quote>>(ENDPOINTS.QUOTES, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getQuote(id: string): Promise<Quote> {
    const response = await apiClient.get<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}`);
    return unwrapResponse(response);
  },

  async createQuote(data: QuoteCreate): Promise<Quote> {
    const response = await apiClient.post<APIResponse<Quote>>(ENDPOINTS.QUOTES, data);
    return unwrapResponse(response);
  },

  async updateQuote(id: string, data: Partial<QuoteCreate>): Promise<Quote> {
    const response = await apiClient.patch<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}`, data);
    return unwrapResponse(response);
  },

  async sendQuote(id: string): Promise<Quote> {
    const response = await apiClient.post<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}/send`);
    return unwrapResponse(response);
  },

  async acceptQuote(id: string): Promise<Quote> {
    const response = await apiClient.post<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}/accept`);
    return unwrapResponse(response);
  },

  async rejectQuote(id: string, reason?: string): Promise<Quote> {
    const response = await apiClient.post<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}/reject`, { reason });
    return unwrapResponse(response);
  },

  async cancelQuote(id: string): Promise<Quote> {
    const response = await apiClient.post<APIResponse<Quote>>(`${ENDPOINTS.QUOTES}/${id}/cancel`);
    return unwrapResponse(response);
  },

  async convertQuoteToInvoice(id: string): Promise<Invoice> {
    const response = await apiClient.post<APIResponse<Invoice>>(`${ENDPOINTS.QUOTES}/${id}/convert-to-invoice`);
    return unwrapResponse(response);
  },

  async getQuotePdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.QUOTES}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Invoices
  // ---------------------------------------------------------------------------

  async listInvoices(params: InvoiceListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<Invoice>>(ENDPOINTS.INVOICES, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getInvoice(id: string): Promise<Invoice> {
    const response = await apiClient.get<APIResponse<Invoice>>(`${ENDPOINTS.INVOICES}/${id}`);
    return unwrapResponse(response);
  },

  async createInvoice(data: InvoiceCreate): Promise<Invoice> {
    const response = await apiClient.post<APIResponse<Invoice>>(ENDPOINTS.INVOICES, data);
    return unwrapResponse(response);
  },

  async validateInvoice(id: string): Promise<Invoice> {
    const response = await apiClient.post<APIResponse<Invoice>>(`${ENDPOINTS.INVOICES}/${id}/validate`, {});
    return unwrapResponse(response);
  },

  async cancelInvoice(id: string, reason: string): Promise<Invoice> {
    const response = await apiClient.post<APIResponse<Invoice>>(`${ENDPOINTS.INVOICES}/${id}/cancel`, { reason });
    return unwrapResponse(response);
  },

  async getInvoicePdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.INVOICES}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async convertProformaToDefinitive(id: string, notes?: string): Promise<Invoice> {
    const response = await apiClient.post<APIResponse<Invoice>>(`${ENDPOINTS.INVOICES}/${id}/convert`, { notes });
    return unwrapResponse(response);
  },

  async getUnpaidInvoices(): Promise<Invoice[]> {
    const response = await apiClient.get<PaginatedResponse<Invoice>>(`${ENDPOINTS.INVOICES}/unpaid`, {
      params: { page_size: 100 },
    });
    return unwrapPaginatedResponse(response).data;
  },

  // ---------------------------------------------------------------------------
  // Payments
  // ---------------------------------------------------------------------------

  async listPayments(params: PaymentListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(ENDPOINTS.PAYMENTS, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getPayment(id: string): Promise<Payment> {
    const response = await apiClient.get<APIResponse<Payment>>(`${ENDPOINTS.PAYMENTS}/${id}`);
    return unwrapResponse(response);
  },

  async createPayment(data: PaymentCreate): Promise<Payment> {
    const response = await apiClient.post<APIResponse<Payment>>(ENDPOINTS.PAYMENTS, data);
    return unwrapResponse(response);
  },

  async validatePayment(id: string): Promise<Payment> {
    const response = await apiClient.post<APIResponse<Payment>>(`${ENDPOINTS.PAYMENTS}/${id}/validate`);
    return unwrapResponse(response);
  },

  async cancelPayment(id: string, reason: string): Promise<Payment> {
    const response = await apiClient.post<APIResponse<Payment>>(`${ENDPOINTS.PAYMENTS}/${id}/reject`, { reason });
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Delivery Notes (BL)
  // ---------------------------------------------------------------------------

  async listDeliveryNotes(params: DeliveryNoteListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<DeliveryNote>>(ENDPOINTS.DELIVERIES, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getDeliveryNote(id: string): Promise<DeliveryNote> {
    const response = await apiClient.get<APIResponse<DeliveryNote>>(`${ENDPOINTS.DELIVERIES}/${id}`);
    return unwrapResponse(response);
  },

  async createDeliveryNote(data: DeliveryNoteCreate): Promise<DeliveryNote> {
    const response = await apiClient.post<APIResponse<DeliveryNote>>(ENDPOINTS.DELIVERIES, data);
    return unwrapResponse(response);
  },

  async confirmDelivery(id: string, data: { signature_client?: string; signature_livreur?: string; nom_receptionnaire: string; observations_livraison?: string }): Promise<DeliveryNote> {
    const response = await apiClient.post<APIResponse<DeliveryNote>>(`${ENDPOINTS.DELIVERIES}/${id}/valider`, data);
    return unwrapResponse(response);
  },

  async getDeliveryNotePdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.DELIVERIES}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default salesService;
